import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth, userMode, isOnboardedLister, isOnboardedRenter } from "@/context/AuthContext";

// One account, two sides of the market. Shown on both profile pages: a chip
// with the current mode and a button that flips to the other one. Each mode has
// a ONE-TIME onboarding, read from persisted flags: entering lister mode
// un-onboarded routes to the Private vs Realtor chooser; entering renter mode
// un-onboarded routes to the regular renter onboarding (/profile). Once a mode
// is onboarded, switching to it is an INSTANT toggle in both directions.
export default function ModeSwitchCard() {
  const { user, switchMode } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!user) return null;
  const mode = userMode(user);
  const target = mode === "lister" ? "renter" : "lister";
  const listerOnboarded = isOnboardedLister(user);
  const renterOnboarded = isOnboardedRenter(user);
  // A realtor whose application is pending/rejected shouldn't be re-routed into
  // onboarding: ListerStatusCard already shows their status. Hide the button.
  const realtorPending =
    user.lister_kind === "realtor" && user.realtor_status !== "approved";
  if (target === "lister" && realtorPending) return null;
  const label =
    target === "lister"
      ? listerOnboarded
        ? "Switch to lister mode"
        : "Become a lister"
      : renterOnboarded
        ? "Switch to renter mode"
        : "Become a renter";

  async function flip() {
    // Server authoritative: attempt the switch and let the backend decide, so a
    // stale client onboarding flag can never wrongly re-open the chooser. The
    // backend returns 409 ONBOARDING_REQUIRED when a lister switch needs setup;
    // a renter switch always succeeds and we route to the profile form only if
    // renter onboarding is not yet complete.
    setBusy(true);
    try {
      const updated = await switchMode(target);
      if (target === "renter" && !isOnboardedRenter(updated || user)) {
        navigate("/profile", { state: { welcome: true } });
        return;
      }
      toast.success(target === "lister" ? "You are in lister mode now." : "You are in renter mode now.");
      navigate(target === "lister" ? "/lister" : "/");
    } catch (error) {
      if (error?.response?.status === 409 &&
          error?.response?.data?.code === "ONBOARDING_REQUIRED") {
        navigate("/become-a-lister", { state: { next: "/lister" } });
        return;
      }
      toast.warning("We could not switch modes. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-brand-line bg-white p-6 shadow-hairline">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-tint text-brand-lime">
          <ArrowLeftRight size={17} />
        </span>
        <div>
          <p className="text-[15px] font-semibold text-brand-ink">
            {mode === "lister" ? "Lister mode" : "Renter mode"}
            <span className="ml-2 rounded-full bg-brand-tint px-2.5 py-0.5 text-xs font-semibold text-brand-lime">
              Current
            </span>
          </p>
          <p className="text-xs text-brand-secondary">
            {mode === "lister"
              ? "You are managing listings. Flip over any time to search as a renter."
              : "You are searching for a home. Flip over any time to list a place."}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={flip}
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2 text-sm font-semibold text-brand-ink transition hover:border-brand-ink disabled:opacity-50"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowLeftRight size={14} />} {label}
      </button>
    </section>
  );
}
